import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Actualiza la configuración de privacidad de un usuario
 * @param userId ID del usuario
 * @param isPrivate Si la cuenta debe ser privada
 * @param allowDirectMessages Si se permiten mensajes directos
 * @returns Promise<void>
 */
export const updatePrivacySettings = async (
  userId: string,
  isPrivate: boolean,
  allowDirectMessages: boolean
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPrivate,
      allowDirectMessages,
      updatedAt: new Date()
    });

    console.log(`Configuración de privacidad actualizada para el usuario ${userId}`);
  } catch (error) {
    console.error('Error al actualizar configuración de privacidad:', error);
    throw new Error('No se pudo actualizar la configuración de privacidad');
  }
};

/**
 * Verifica si un usuario puede ver el contenido de otro usuario
 * @param viewerId ID del usuario que intenta ver el contenido
 * @param ownerId ID del usuario propietario del contenido
 * @returns Promise<boolean> True si puede ver el contenido, false en caso contrario
 */
export const canViewUserContent = async (
  viewerId: string | undefined,
  ownerId: string
): Promise<boolean> => {
  try {
    // Si es el mismo usuario, siempre puede ver su propio contenido
    if (viewerId === ownerId) {
      return true;
    }

    // Obtener datos del usuario propietario
    const ownerRef = doc(db, 'users', ownerId);
    const ownerDoc = await getDoc(ownerRef);

    if (!ownerDoc.exists()) {
      console.log(`Usuario ${ownerId} no encontrado`);
      return false;
    }

    const ownerData = ownerDoc.data();

    // Si la cuenta no es privada, cualquiera puede ver el contenido
    if (!ownerData.isPrivate) {
      return true;
    }

    // Si el usuario no está autenticado, no puede ver contenido privado
    if (!viewerId) {
      return false;
    }

    // Verificar si el viewer sigue al propietario
    const followerRef = doc(db, `users/${ownerId}/followers`, viewerId);
    const followerDoc = await getDoc(followerRef);

    return followerDoc.exists();
  } catch (error) {
    console.error('Error al verificar permisos de visualización:', error);
    return false;
  }
};

/**
 * Verifica si un usuario puede enviar mensajes directos a otro usuario
 * @param senderId ID del usuario que intenta enviar el mensaje
 * @param recipientId ID del usuario destinatario
 * @returns Promise<{canSend: boolean, reason?: string}> Resultado y razón si no puede
 */
export const canSendDirectMessage = async (
  senderId: string,
  recipientId: string
): Promise<{ canSend: boolean, reason?: string }> => {
  try {
    // Si es el mismo usuario, no puede enviarse mensajes a sí mismo
    if (senderId === recipientId) {
      return { canSend: false, reason: 'No puedes enviarte mensajes a ti mismo' };
    }

    // Obtener datos del usuario destinatario
    const recipientRef = doc(db, 'users', recipientId);
    const recipientDoc = await getDoc(recipientRef);

    if (!recipientDoc.exists()) {
      return { canSend: false, reason: 'Usuario no encontrado' };
    }

    const recipientData = recipientDoc.data();

    // Si el destinatario permite mensajes directos de cualquiera
    if (recipientData.allowDirectMessages) {
      return { canSend: true };
    }

    // Si el destinatario solo permite mensajes de seguidores, verificar si el remitente es seguidor
    const followerRef = doc(db, `users/${recipientId}/followers`, senderId);
    const followerDoc = await getDoc(followerRef);

    if (followerDoc.exists()) {
      return { canSend: true };
    }

    return {
      canSend: false,
      reason: 'Este usuario solo permite mensajes de personas que sigue'
    };
  } catch (error) {
    console.error('Error al verificar permisos de mensajería:', error);
    return {
      canSend: false,
      reason: 'Error al verificar permisos. Inténtalo de nuevo más tarde.'
    };
  }
};

/**
 * Actualiza la preferencia de filtro de posts del usuario
 * @param userId ID del usuario
 * @param filter Filtro preferido: 'recent', 'popular', 'oldest', 'following', 'trending'
 * @returns Promise<void>
 */
export const updatePreferredPostFilter = async (
  userId: string,
  filter: 'recent' | 'popular' | 'oldest' | 'following' | 'trending'
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      preferredPostFilter: filter,
      updatedAt: new Date()
    });

    console.log(`Filtro preferido actualizado para el usuario ${userId}: ${filter}`);
  } catch (error) {
    console.error('Error al actualizar filtro preferido:', error);
    throw new Error('No se pudo actualizar el filtro preferido');
  }
};

/**
 * Obtiene el filtro preferido de posts del usuario
 * @param userId ID del usuario
 * @returns Promise<string> Filtro preferido o 'following' por defecto
 */
export const getPreferredPostFilter = async (
  userId: string
): Promise<'recent' | 'popular' | 'oldest' | 'following' | 'trending'> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return 'following';
    }

    const userData = userDoc.data();
    return userData.preferredPostFilter || 'following';
  } catch (error) {
    console.error('Error al obtener filtro preferido:', error);
    return 'following';
  }
};
